import { memo, FC, useCallback, useState } from 'react'
import { AiAssistantThreadList } from '../aiAssistantThreadList/aiAssistantThreadList'
import { useLazyGetChatRoomByEntityQuery } from '@crew/apis/chat/chatApis'
import { useGetOrInsertProjectForAiAssistantChannelMutation } from '@crew/apis/project/projectApis'
import { ChatView } from '@crew/enums/app'
import { EntityType } from '@crew/enums/domain'
import { Project } from '@crew/models/domain'
import { Chat, useAiAssistantService } from '@crew/states'
import { useEffectOnce } from '@dx-system/react-use'
import { useAppDispatch, useAppSelector } from 'states/hooks'
import { AiAssistantThread } from '../aiAssistantThread/aiAssistantThread'
import { AiAssistantChannelDisplayType } from '@crew/enums/app'
import {
  closeAiAssistantWindow,
  setIsAiAssistantWindowLargeHeight,
} from 'features/app/states/appSlice'
import { useTranslation } from '@crew/modules/i18n'
import { CrewAiAssistantIcon } from 'components/elements/crewAiAssistantIcon/crewAiAssistantIcon'
import Close from '~icons/material-symbols/close'
import BaselineFullscreen from '~icons/ic/baseline-fullscreen'
import BaselineFullscreenExit from '~icons/ic/baseline-fullscreen-exit'

/**
 * AIアシスタントウィンドウパネル
 */
export const AiAssistantWindowPanel: FC = memo(() => {
  // Sliceの操作を行うためのServiceを取得
  const dispatch = useAppDispatch()
  const aiAssistantService = useAiAssistantService(dispatch)

  const { t } = useTranslation()

  // AIアシスタントウィンドウが最大サイズかどうか
  const isAiAssistantWindowLargeHeight = useAppSelector(
    (state) => state.app.isAiAssistantWindowLargeHeight
  )

  // AIアシスタントチャンネル取得用
  const [getOrInsertProjectForAiAssistantChannel] =
    useGetOrInsertProjectForAiAssistantChannelMutation()

  // チャットルーム取得用
  const [lazyGetChatRoomByEntityQuery, lazyGetChatRoomByEntityQueryResult] =
    useLazyGetChatRoomByEntityQuery()

  const [aiAssistantChannel, setAiAssistantChannel] = useState<
    Project | undefined
  >(undefined)

  // 表示対象のトピックIDを取得する
  const currentAiAssistantChatThread = useAppSelector(
    (state) => state.message.chat.aiAssistantChannel.currentChatThread
  )
  const currentTopicId = currentAiAssistantChatThread?.topicId

  // トピックIDが指定されていればスレッド表示、そうでない場合はスレッドリスト表示する
  const currentAiAssistantView = currentTopicId
    ? ChatView.Thread
    : ChatView.ThreadList

  // AIアシスタントのチャットルームを取得する
  const getAiAssistantChatRoom = useCallback(async () => {
    const result = await getOrInsertProjectForAiAssistantChannel().unwrap()
    if (!result.aiAssistantChannel) {
      return
    }

    // AIアシスタントチャンネルをローカルステートに設定
    setAiAssistantChannel(result.aiAssistantChannel)

    const params = {
      entityType: EntityType.Project,
      entityRecordId: result.aiAssistantChannel.id,
    }
    await lazyGetChatRoomByEntityQuery(params)
  }, [getOrInsertProjectForAiAssistantChannel, lazyGetChatRoomByEntityQuery])

  // 画面を開いたらAIアシスタントチャンネル取得を実行する
  useEffectOnce(() => {
    getAiAssistantChatRoom()
  })

  const renderAiAssistantPanel = useCallback(() => {
    // 現在の表示形式に応じて、表示するコンポーネントを切り替える
    switch (currentAiAssistantView) {
      case ChatView.ThreadList: {
        return (
          <AiAssistantThreadList
            displayType={AiAssistantChannelDisplayType.AiAssistantWindow} // AIアシスタントウィンドウ
          />
        )
      }
      case ChatView.Thread: {
        return (
          <AiAssistantThread
            displayType={AiAssistantChannelDisplayType.AiAssistantWindow}
          />
        )
      }
    }
  }, [currentAiAssistantView])

  // 共通ヘッダーのウィンドウ閉じるボタンを押したときの処理
  const handleCloseAiAssistantWindowButtonClick = useCallback(() => {
    dispatch(closeAiAssistantWindow())
  }, [dispatch])

  // AIアシスタントウィンドウの拡大ボタン押下の処理
  const handleExpandAiAssistantWindowButtonClick = useCallback(() => {
    dispatch(setIsAiAssistantWindowLargeHeight(true))
  }, [dispatch])

  // AIアシスタントウィンドウの縮小ボタン押下の処理
  const handleShrinkAiAssistantWindowButtonClick = useCallback(() => {
    dispatch(setIsAiAssistantWindowLargeHeight(false))
  }, [dispatch])

  // undefinedチェック・nullチェックを行うにあたり説明変数に格納
  const aiAssistantChatRoomEntity =
    lazyGetChatRoomByEntityQueryResult.data?.chatRoom

  // AIアシスタントのチャットルームが取得できていない場合は何も描画しない
  if (!aiAssistantChatRoomEntity || !aiAssistantChannel) {
    return null
  }

  // AIアシスタントチャンネルのチャットルーム情報をReduxに設定
  const aiAssistantChatRoom: Chat.AiAssistantChannelChatRoom = {
    id: aiAssistantChatRoomEntity.id,
    rootEntityType: aiAssistantChatRoomEntity.rootEntityType,
    rootEntityRecordId: aiAssistantChatRoomEntity.rootEntityRecordId,
    entityType: EntityType.Project,
    entityRecordId: aiAssistantChannel.id, // AIアシスタントチャンネルはプロジェクトでありentityRecordIdとrootEntityRecordIdは同じ値を取る
  }
  aiAssistantService.setChatRoom(aiAssistantChatRoom)

  // パネル内を描画
  return (
    <div className="flex flex-col w-full h-full">
      {/** 共通のヘッダー + ×ボタン */}

      <div className="flex flex-row gap-x-2.5 items-center p-2.5 border-b crew-border-gray crew-bg-gray-1 rounded-t-lg">
        <div>
          <CrewAiAssistantIcon width={24} height={24} />
        </div>
        <div>{t('label.aiAssistant')}</div>
        <div className="ml-auto flex flex-row gap-x-2.5">
          <div className="crew-action-base p-1 hover:bg-crew-gray-200 hover:dark:bg-crew-gray-700">
            {/** AIアシスタントウィンドウの拡大縮小ボタン */}
            {/** 拡大状態であれば縮小ボタン、縮小状態であれば拡大ボタンを表示する */}
            {isAiAssistantWindowLargeHeight ? (
              <BaselineFullscreenExit
                width={24}
                height={24}
                onClick={handleShrinkAiAssistantWindowButtonClick}
              />
            ) : (
              <BaselineFullscreen
                width={24}
                height={24}
                onClick={handleExpandAiAssistantWindowButtonClick}
              />
            )}
          </div>
          {/** AIアシスタントウィンドウ閉じるボタン */}
          <div className="crew-action-base p-1 hover:bg-crew-gray-200 hover:dark:bg-crew-gray-700">
            <Close
              width={24}
              height={24}
              onClick={handleCloseAiAssistantWindowButtonClick}
            />
          </div>
        </div>
      </div>
      {/** メッセージリスト */}
      {renderAiAssistantPanel()}
    </div>
  )
})
